import { Howl, Howler } from "howler";
import { store } from "./store";
import { hasInteracted, markInteraction, onFirstInteraction } from "./firstInteraction";

// One place that decides whether anything is audible: the player's own mute switch (kept in
// localStorage), plus the portal's mute setting and "an ad is playing" (set by portal.ts).

const KEY = "rafiki.muted";
let userMuted = false;
try {
  userMuted = store.getItem(KEY) === "1";
} catch (e) {}
let portalMuted = false;
const listeners = new Set<(muted: boolean) => void>();

const apply = () => {
  try {
    Howler.mute(userMuted || portalMuted);
  } catch (e) {}
  listeners.forEach((l) => l(userMuted));
};
apply();

// Playgama: the saved mute switch arrives from the Bridge after this file has loaded.
export const reloadUserMuted = () => {
  userMuted = store.getItem(KEY) === "1";
  apply();
};

export const setPortalMuted = (muted: boolean) => {
  portalMuted = muted;
  apply();
};

export const isUserMuted = () => userMuted;
export const setUserMuted = (muted: boolean) => {
  userMuted = muted;
  try {
    store.setItem(KEY, muted ? "1" : "0");
  } catch (e) {}
  apply();
  // Sound back on: make sure the music is actually going (the page may have lost it while hidden).
  if (!muted) resumeAudio();
};
export const toggleUserMuted = () => setUserMuted(!userMuted);
export const onMuteChange = (l: (muted: boolean) => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

// Phones only start audio after a tap: resume the audio context on the first real gesture
// (Play, Ready) so the effects that follow are heard. Howler's own autoUnlock also listens, but
// an explicit resume covers iOS Safari when the first sound comes from a timer, not a tap.
export const unlockAudio = () => {
  try {
    Howler.autoUnlock = true;
    const ctx: AudioContext | undefined = (Howler as any).ctx;
    if (ctx && ctx.state !== "running") ctx.resume().catch(() => undefined);
    // play-and-stop a silent buffer: the gesture is what matters on WebKit
    if (ctx && ctx.state === "running") {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, 22050);
      src.connect(ctx.destination);
      src.start(0);
    }
  } catch (e) {}
};

// A quiet, original lo-fi loop under the game on the portal builds (the website stays as it
// was: sound effects only). Starts on the first tap, which is what browsers require anyway.
let music: Howl | null = null;
// Whether the game wants music right now (the table is on screen). Separate from whether it is
// actually audible: leaving the page suspends the audio context, and the user can mute.
let musicWanted = false;
const MUSIC_VOLUME = 0.18;
// Ducked to ~0.3x while the theme tune or its "...with Rafiki!" call plays over it.
const DUCK = 0.3;
let duckDepth = 0;
// The start screen's theme tune is playing: the loop waits for it rather than playing over it.
let holdingMusic = false;
// play() was called but the file is still loading: Howler queues it, playing() is still false,
// and a second play() would queue a second copy of the loop.
let musicPending = false;
const musicVolume = () => (duckDepth > 0 ? MUSIC_VOLUME * DUCK : MUSIC_VOLUME);
const musicOnThisBuild = () =>
  // Same test as portal.ts (not imported, to keep the two modules independent).
  ["crazygames", "gamedistribution", "playgama", "gamepix", "y8"].includes(process.env.REACT_APP_PORTAL || "");

const playMusic = (fadeIn = 0) => {
  if (!musicWanted || holdingMusic || !musicOnThisBuild()) return;
  // Nothing can be heard before the first tap anyway: the file (about 265 KB) is only fetched
  // then, not while the table is still loading (a new portal player is dealt in without a tap).
  // The tap itself starts it (onGesture below).
  if (!music && !hasInteracted()) return;
  if (!music) {
    music = new Howl({
      src: [`${process.env.PUBLIC_URL}/sounds/music.webm`, `${process.env.PUBLIC_URL}/sounds/music.mp3`],
      loop: true,
      volume: MUSIC_VOLUME,
    });
  }
  if (!music.playing() && !musicPending) {
    musicPending = true;
    music.once("play", () => {
      musicPending = false;
    });
    music.once("playerror", () => {
      musicPending = false;
    });
    if (fadeIn > 0) {
      music.volume(0);
      music.play();
      music.fade(0, musicVolume(), fadeIn);
    } else {
      music.volume(musicVolume());
      music.play();
    }
  }
};

export const startMusic = () => {
  unlockAudio();
  if (!musicOnThisBuild()) return;
  musicWanted = true;
  playMusic();
};
export const stopMusic = () => {
  musicWanted = false;
  if (music?.playing()) music.fade(music.volume(), 0, 600);
  setTimeout(() => {
    if (music && !musicWanted && music.volume() === 0) {
      music.stop();
      music.volume(MUSIC_VOLUME);
    }
  }, 700);
};

// Turn the loop down while a jingle plays over it. Returns the function that turns it back up;
// calling it more than once is harmless. Nested ducks only restore once the last one ends.
export const duckMusic = (): (() => void) => {
  duckDepth++;
  if (music?.playing() && musicWanted) music.fade(music.volume(), musicVolume(), 200);
  let done = false;
  return () => {
    if (done) return;
    done = true;
    duckDepth = Math.max(0, duckDepth - 1);
    if (duckDepth === 0 && music?.playing() && musicWanted) music.fade(music.volume(), MUSIC_VOLUME, 600);
  };
};

// Keep the loop from starting while the theme tune plays on the way into a game. Returns the
// release: the loop then fades in (if the table still wants it). Safe to call more than once.
export const holdMusic = (): (() => void) => {
  holdingMusic = true;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    holdingMusic = false;
    playMusic(1200);
  };
};

// Coming back to the page: browsers (iOS Safari above all) suspend or "interrupt" the audio
// context while the tab is hidden, and the loop never comes back on its own. So on return, and
// on the next tap (WebKit only lets a gesture resume it), resume the context and restart the
// music if the game still wants it. Howler's own autoResume doesn't cover the interrupted state.
export const resumeAudio = () => {
  try {
    const ctx: AudioContext | undefined = (Howler as any).ctx;
    if (ctx && ctx.state !== "running") {
      ctx.resume().then(() => playMusic(), () => undefined);
    }
  } catch (e) {}
  playMusic();
};

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      resumeAudio();
    } else if (music?.playing()) {
      // Pause rather than let the loop play on in the background; resumeAudio restarts it.
      music.pause();
    }
  });
  window.addEventListener("pageshow", () => resumeAudio());
  // The next tap after coming back is the gesture WebKit wants before it will play again.
  const onGesture = () => {
    markInteraction();
    if (!musicWanted) return;
    resumeAudio();
  };
  document.addEventListener("pointerdown", onGesture, { passive: true, capture: true });
  document.addEventListener("touchend", onGesture, { passive: true, capture: true });
  // The loop asked for before the player's first tap, click or key starts with it (playMusic
  // doesn't fetch the file before then).
  onFirstInteraction(() => playMusic());
}
